import React, { useMemo, useState } from 'react'
import { useContext } from 'react'
import useStep from '../../hooks/use-step'
import { GbsStore } from '../../store'
import { GbsStep } from '../../store/interfaces'
import { useHistory } from 'react-router-dom'
import {
  BeginOnboardingButton,
  CongratulationsContent,
  CongratulationsHeader,
  CongratulationsP,
  ContributionData,
  ContributionLabel,
  EditSelectionLink,
  LearnAboutLink,
  ResourceWrapper,
  SectionTitle
} from './styles'

import { CostInformation, PlanWrapper } from '../../components/PlanInformation'
import { Resource } from '../../components/Resource'
import { Paths } from '../../routes/paths'
import { planService } from '../../services'
import { useQuery } from 'react-query'
import {
  CenteredSpinner,
  Modal,
  SummaryOfBenefitsCoverage,
  useErrorHandler,
  useToaster
} from 'react-angle-dashboard-components'
import { ISelectablePlan } from '../../interfaces/plan'
import { capitalize } from '@angle/utils'

// whiteBg?: boolean
// icon: string
// title: string
// subtitle?: string

const resources = [
  {
    whiteBg: true,
    icon: 'icon-doctor',
    title: 'Find Your Doctor',
    onClick: () => window.open(Paths.publicProviders, '_blank')
  },
  {
    whiteBg: true,
    icon: 'icon-facility',
    title: 'Find Facilities',
    onClick: () => window.open(Paths.publicFacilities, '_blank')
  }
]

const stepKey = 'congratulations'

export const Congratulations = () => {
  const history = useHistory()
  const { dispatch } = useContext(GbsStore)
  const [show, setShow] = useState(false)
  const [currentSBCData, setSBCData] = useState<any>(null)
  const [sbcIsLoading, setSbcLoading] = useState(false)
  const toaster = useToaster()

  const {
    data: plans,
    isLoading,
    error
  } = useQuery(['selectedPlans'], () =>
    planService.getSelectablePlans({ selected: 1 })
  )

  // get cheapest plan total employer premium to determine 'starting at' cost
  const cheapestPlanTotal = plans?.length
    ? plans[plans.length - 1].totalEmployerCost
    : 0

  const handleShowPlanDetails = async (id: string, plan: ISelectablePlan) => {
    try {
      setSbcLoading(true)
      setShow(true)
      const { events, faqs } = await planService.getSBC(id)

      setSBCData({
        benefits: events,
        faqs,
        plan,
        coverageInfo: {
          plan: {
            name: plan.name,
            metalTier: plan?.tier,
            type: plan?.type
          },
          coveragePeriod: {
            from: plan?.startDate,
            to: plan?.endDate
          },
          coverageFor: plan?.eligibleMember?.map(capitalize).join('/') //'Single/Family'
        }
      })
      setSbcLoading(false)
    } catch (e) {
      setSbcLoading(false)
      setShow(false)
      toaster.show({
        message: "We can't get the SBC information",
        icon: 'icon-cancel',
        type: 'danger'
      })
    }
  }

  useErrorHandler(error)

  const stepConfig: Omit<GbsStep, 'data'> = useMemo(() => {
    return {
      bottomBar: {
        hidden: true
      },
      topBar: {
        hidden: true
      }
    }
  }, [])

  useStep(stepKey, stepConfig, dispatch)

  return (
    <>
      <CongratulationsHeader>
        <h1 className="text-white">Congratulations!</h1>
      </CongratulationsHeader>
      <CongratulationsContent>
        <CongratulationsP>
          We’ve estimated your costs and benefits based on your plan
          selection(s) and employee information. You’re ready to begin
          onboarding and enrollment!
        </CongratulationsP>
        <SectionTitle>Your total employer contribution</SectionTitle>
        {isLoading ? (
          <CenteredSpinner />
        ) : (
          <div>
            <div>
              <ContributionLabel>Estimated</ContributionLabel>
              <ContributionData style={{ fontSize: 45 }}>
                $<span>{cheapestPlanTotal?.toLocaleString()}</span>
                /mo
              </ContributionData>
            </div>
            {/* TODO: need this breakdown on backend */}
            {/* <div>
            <ContributionLabel>Employees</ContributionLabel>
            <ContributionData>$3,100</ContributionData>
          </div>
          <div>
            <ContributionLabel>Dependents</ContributionLabel>
            <ContributionData>$1,027</ContributionData>
          </div> */}
          </div>
        )}

        <BeginOnboardingButton
          onClick={() => history.push(Paths.companyCensus)}
        >
          Begin Onboarding
        </BeginOnboardingButton>
        <LearnAboutLink to={Paths.finalQuoteDetails}>
          Learn About My Plan Details
        </LearnAboutLink>
        <div className="d-flex justify-content-center flex-wrap">
          {plans?.map((plan) => (
            <PlanWrapper style={{ height: 525, marginRight: 30 }} key={plan.id}>
              <CostInformation
                plan={plan}
                compressedVersion
                onShowPlanDetails={handleShowPlanDetails}
              />
            </PlanWrapper>
          ))}
        </div>
        <EditSelectionLink to="/plans" className="mt-3 mb-4">
          Edit My Plan Selection
        </EditSelectionLink>
        <SectionTitle>Resources</SectionTitle>
        <div className="d-flex justify-content-between">
          {resources.map((resource) => (
            <ResourceWrapper key={resource.title}>
              <Resource {...resource} />
            </ResourceWrapper>
          ))}
        </div>
      </CongratulationsContent>
      <Modal
        show={show}
        onHide={() => setShow(false)}
        dialogClassName="w-100 m-0 mw-100"
        className="px-0"
        aria-labelledby="example-custom-modal-styling-title"
        style={{ zIndex: 9999 }}
      >
        <Modal.Header closeButton></Modal.Header>
        <Modal.Body style={{ minHeight: '95vh' }}>
          {sbcIsLoading && <CenteredSpinner />}
          {currentSBCData && !sbcIsLoading && (
            <SummaryOfBenefitsCoverage
              coverageInfo={currentSBCData.coverageInfo}
              benefits={currentSBCData.benefits}
              faqs={currentSBCData.faqs}
            />
          )}
        </Modal.Body>
      </Modal>
    </>
  )
}

export default Congratulations
